import React, { useState, useEffect } from 'react';
import { Plus, Building, TrendingUp, Edit, Trash2, DollarSign, Activity, TrendingDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import WithdrawalsManagement from './WithdrawalsManagement';

const BankrollManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('casas'); // 'casas' ou 'saques'
  const [bettingHouses, setBettingHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDepositsHistoryModal, setShowDepositsHistoryModal] = useState(false);
  const [showEditDepositModal, setShowEditDepositModal] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [depositingHouse, setDepositingHouse] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all_time'); // Padrão: Todo o Período
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [allBets, setAllBets] = useState([]); // Todas as apostas do usuário
  const [allDeposits, setAllDeposits] = useState([]); // Histórico de depósitos
  const [allWithdrawals, setAllWithdrawals] = useState([]); // Histórico de saques
  const [formData, setFormData] = useState({
    name: '',
    initial_bankroll: '',
    current_bankroll: ''
  });

  const [stats, setStats] = useState({
    totalInitial: 0,
    totalCurrent: 0,
    totalProfit: 0
  });

  useEffect(() => {
    if (user) {
      loadBettingHouses();
      loadAllBets(); // Carregar apostas para filtro
      loadAllDeposits(); // Carregar histórico de depósitos
      loadAllWithdrawals(); // Carregar histórico de saques
      migrateExistingDeposits(); // Migrar depósitos existentes (executa apenas uma vez)
    }
  }, [user, selectedPeriod, customStartDate, customEndDate]);

  useEffect(() => {
    calculateStats();
  }, [bettingHouses, allBets, allWithdrawals, selectedPeriod, customStartDate, customEndDate]);

  const getPeriodDates = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedPeriod) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'all_time':
        return null; // No filter - show all data
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const loadAllBets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setAllBets(data);
      }
    } catch (error) {
      console.error('Error loading bets:', error);
    }
  };

  const loadAllWithdrawals = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/withdrawals?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setAllWithdrawals(data);
      }
    } catch (error) {
      console.error('Error loading withdrawals:', error);
    }
  };

  const loadAllDeposits = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setAllDeposits(data);
      }
    } catch (error) {
      console.error('Error loading deposits:', error);
    }
  };

  const migrateExistingDeposits = async () => {
    try {
      // Verificar se já existe histórico
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.length === 0) {
          // Se não houver histórico, migrar
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits/migrate?user_id=${user.id}`, {
            method: 'POST'
          });
          // Recarregar depósitos após migração
          loadAllDeposits();
        }
      }
    } catch (error) {
      console.error('Error migrating deposits:', error);
    }
  };

  const calculateStats = () => {
    const periodDates = getPeriodDates();
    
    // Filtrar depósitos pelo período
    let filteredDeposits = allDeposits;
    if (periodDates) {
      const start = new Date(periodDates.startDate);
      const end = new Date(periodDates.endDate);
      filteredDeposits = allDeposits.filter(deposit => {
        const depositDate = new Date(deposit.date);
        return depositDate >= start && depositDate <= end;
      });
    }

    // Calcular investimento total do período (soma dos depósitos)
    const totalInitial = filteredDeposits.reduce((sum, deposit) => 
      sum + (deposit.amount || 0), 0
    );
    
    // Filtrar apostas pelo período
    let filteredBets = allBets;
    if (periodDates) {
      const start = new Date(periodDates.startDate);
      const end = new Date(periodDates.endDate);
      filteredBets = allBets.filter(bet => {
        const betDate = new Date(bet.date);
        return betDate >= start && betDate <= end;
      });
    }

    // Calcular resultado por casa de apostas no período
    const houseProfits = {};
    filteredBets.forEach(bet => {
      if (!houseProfits[bet.betting_house_id]) {
        houseProfits[bet.betting_house_id] = 0;
      }
      houseProfits[bet.betting_house_id] += bet.profit || 0;
    });

    // Calcular depósitos por casa até o período
    const houseDeposits = {};
    let allDepositsUpToPeriod = allDeposits;
    if (periodDates) {
      const end = new Date(periodDates.endDate);
      allDepositsUpToPeriod = allDeposits.filter(deposit => {
        const depositDate = new Date(deposit.date);
        return depositDate <= end;
      });
    }
    
    allDepositsUpToPeriod.forEach(deposit => {
      if (!houseDeposits[deposit.betting_house_id]) {
        houseDeposits[deposit.betting_house_id] = 0;
      }
      houseDeposits[deposit.betting_house_id] += deposit.amount || 0;
    });

    // 🆕 Calcular saques por casa até o período
    const houseWithdrawals = {};
    let allWithdrawalsUpToPeriod = allWithdrawals;
    if (periodDates) {
      const end = new Date(periodDates.endDate);
      allWithdrawalsUpToPeriod = allWithdrawals.filter(withdrawal => {
        const withdrawalDate = new Date(withdrawal.date);
        return withdrawalDate <= end;
      });
    }
    
    allWithdrawalsUpToPeriod.forEach(withdrawal => {
      if (!houseWithdrawals[withdrawal.betting_house_id]) {
        houseWithdrawals[withdrawal.betting_house_id] = 0;
      }
      houseWithdrawals[withdrawal.betting_house_id] += withdrawal.amount || 0;
    });

    // Calcular banca atual
    let totalCurrent = 0;
    bettingHouses.forEach(house => {
      const depositsUpToPeriod = houseDeposits[house.id] || 0;
      const withdrawalsUpToPeriod = houseWithdrawals[house.id] || 0;
      
      // Calcular lucro/prejuízo até o final do período
      let profitUpToPeriod = 0;
      let betsUpToPeriod = allBets.filter(bet => bet.betting_house_id === house.id);
      
      if (periodDates) {
        const end = new Date(periodDates.endDate);
        betsUpToPeriod = betsUpToPeriod.filter(bet => {
          const betDate = new Date(bet.date);
          return betDate <= end;
        });
      }
      
      profitUpToPeriod = betsUpToPeriod.reduce((sum, bet) => sum + (bet.profit || 0), 0);
      
      // Calcular valor em apostas pendentes
      const pendingBets = allBets.filter(bet => 
        bet.betting_house_id === house.id && 
        bet.status === 'pending'
      );
      const pendingAmount = pendingBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
      
      // 🔧 CORREÇÃO: Banca atual = depósitos + lucro - apostas pendentes - saques
      totalCurrent += depositsUpToPeriod + profitUpToPeriod - pendingAmount - withdrawalsUpToPeriod;
    });
    
    const totalProfit = totalCurrent - (periodDates ? totalInitial : allDeposits.reduce((sum, d) => sum + d.amount, 0));

    setStats({ totalInitial, totalCurrent, totalProfit });
  };

  // Calcular estatísticas detalhadas de uma casa específica
  const getHouseStats = (houseId) => {
    const periodDates = getPeriodDates();
    
    // Filtrar apostas pelo período e pela casa
    let houseBets = allBets.filter(bet => bet.betting_house_id === houseId);
    
    if (periodDates) {
      const start = new Date(periodDates.startDate);
      const end = new Date(periodDates.endDate);
      houseBets = houseBets.filter(bet => {
        const betDate = new Date(bet.date);
        return betDate >= start && betDate <= end;
      });
    }

    const totalBets = houseBets.length;
    const greenBets = houseBets.filter(b => b.result === 'green').length;
    const redBets = houseBets.filter(b => b.result === 'red').length;
    const totalVolume = houseBets.reduce((sum, b) => sum + (b.stake || 0), 0);
    const totalProfit = houseBets.reduce((sum, b) => sum + (b.profit || 0), 0);
    const winRate = totalBets > 0 ? (greenBets / totalBets * 100) : 0;
    const roi = totalVolume > 0 ? (totalProfit / totalVolume * 100) : 0;

    return {
      totalBets,
      greenBets,
      redBets,
      totalVolume,
      totalProfit,
      winRate,
      roi
    };
  };

  const loadBettingHouses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBettingHouses(data);
      }
    } catch (error) {
      console.error('Error loading betting houses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openModal = (house = null) => {
    if (house) {
      setEditingHouse(house);
      setFormData({
        name: house.name,
        initial_bankroll: house.initial_bankroll.toString(),
        current_bankroll: house.current_bankroll.toString()
      });
    } else {
      setEditingHouse(null);
      setFormData({ name: '', initial_bankroll: '', current_bankroll: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHouse(null);
    setFormData({ name: '', initial_bankroll: '', current_bankroll: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        initial_bankroll: parseFloat(formData.initial_bankroll),
        current_bankroll: parseFloat(formData.current_bankroll || formData.initial_bankroll)
      };

      let response;
      if (editingHouse) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses/${editingHouse.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: payload.name,
            current_bankroll: payload.current_bankroll
          })
        });
      } else {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingHouse ? 'Casa de apostas atualizada com sucesso!' : 'Casa de apostas criada com sucesso!');
        closeModal();
        loadBettingHouses();
      } else {
        throw new Error('Falha ao salvar');
      }
    } catch (error) {
      console.error('Error saving betting house:', error);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const openDepositModal = (house) => {
    setDepositingHouse(house);
    setDepositAmount('');
    setShowDepositModal(true);
  };

  const closeDepositModal = () => {
    setDepositingHouse(null);
    setDepositAmount('');
    setShowDepositModal(false);
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const depositValue = parseFloat(depositAmount);
      
      if (depositValue <= 0) {
        alert('O valor do depósito deve ser maior que zero!');
        return;
      }
      
      // Usar novo endpoint específico para depósitos
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses/${depositingHouse.id}/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: depositValue,
          date: depositDate
        })
      });

      if (response.ok) {
        const updatedHouse = await response.json();
        const originalCapital = depositingHouse.initial_bankroll - (depositingHouse.additional_deposits || 0);
        const totalDeposits = updatedHouse.additional_deposits || 0;
        const totalInvested = updatedHouse.initial_bankroll;
        const currentBalance = updatedHouse.current_bankroll;
        const result = currentBalance - totalInvested;
        
        alert(
          `💰 Depósito de R$ ${depositValue.toFixed(2)} adicionado com sucesso!\n\n` +
          `📊 RESUMO DA BANCA:\n` +
          `━━━━━━━━━━━━━━━━━━━━\n` +
          `💵 Capital Inicial: R$ ${originalCapital.toFixed(2)}\n` +
          `➕ Depósitos Adicionais: R$ ${totalDeposits.toFixed(2)}\n` +
          `📈 Capital Total Investido: R$ ${totalInvested.toFixed(2)}\n` +
          `━━━━━━━━━━━━━━━━━━━━\n` +
          `💰 Banca Atual: R$ ${currentBalance.toFixed(2)}\n` +
          `${result >= 0 ? '🟢' : '🔴'} Resultado: R$ ${result.toFixed(2)}\n` +
          `${result >= 0 ? '📈' : '📉'} ROI: ${((result / totalInvested) * 100).toFixed(1)}%`
        );
        
        loadBettingHouses();
        loadAllDeposits();
        closeDepositModal();
      } else {
        throw new Error('Falha ao adicionar depósito');
      }
    } catch (error) {
      console.error('Error adding deposit:', error);
      alert('Erro ao adicionar depósito. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (houseId) => {
    if (!confirm('Tem certeza que deseja excluir esta casa de apostas?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses/${houseId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Casa de apostas excluída com sucesso!');
        loadBettingHouses();
      } else {
        throw new Error('Falha ao excluir');
      }
    } catch (error) {
      console.error('Error deleting betting house:', error);
      alert('Erro ao excluir. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeposit = (deposit) => {
    setEditingDeposit(deposit);
    setDepositAmount(deposit.amount.toString());
    setDepositDate(deposit.date);
    setShowEditDepositModal(true);
  };

  const handleUpdateDeposit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits/${editingDeposit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(depositAmount),
          date: depositDate,
          description: editingDeposit.description
        })
      });

      if (response.ok) {
        alert('Depósito atualizado com sucesso!');
        loadAllDeposits();
        loadBettingHouses();
        setShowEditDepositModal(false);
        setEditingDeposit(null);
      }
    } catch (error) {
      console.error('Error updating deposit:', error);
      alert('Erro ao atualizar depósito.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeposit = async (depositId) => {
    if (!confirm('Tem certeza que deseja excluir este depósito?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits/${depositId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Depósito excluído com sucesso!');
        loadAllDeposits();
        loadBettingHouses();
      }
    } catch (error) {
      console.error('Error deleting deposit:', error);
      alert('Erro ao excluir depósito.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateBankrolls = async () => {
    if (!confirm('🔧 Recalcular todos os valores?\n\nIsso irá corrigir possíveis inconsistências nos saldos das casas de apostas baseado no histórico de apostas.\n\nDeseja continuar?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses/recalculate-bankrolls?user_id=${user.id}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Sucesso!\n\n${data.message}\n\nOs valores foram recalculados corretamente.`);
        // Recarregar dados
        loadBettingHouses();
        loadAllDeposits();
      } else {
        throw new Error('Erro ao recalcular');
      }
    } catch (error) {
      console.error('Error recalculating bankrolls:', error);
      alert('❌ Erro ao recalcular valores. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Gestão de Banca
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Gerencie suas bancas em diferentes casas de apostas
          </p>
        </div>
        {activeTab === 'casas' && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => openModal()}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span className="text-sm sm:text-base">Nova Casa de Apostas</span>
            </button>
            <button
              onClick={() => setShowDepositsHistoryModal(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-teal-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-green-700 hover:to-teal-700 transition-colors flex items-center justify-center space-x-2"
            >
              <DollarSign className="w-5 h-5" />
              <span className="text-sm sm:text-base">Gerenciar Depósitos</span>
            </button>
            <button
              onClick={handleRecalculateBankrolls}
              disabled={loading}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-orange-700 hover:to-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <Activity className="w-5 h-5" />
              <span className="text-sm sm:text-base">
                {loading ? 'Recalculando...' : '🔧 Recalcular Valores'}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setActiveTab('casas')}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'casas'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Building className="w-5 h-5" />
            <span>Casas de Apostas</span>
          </button>
          <button
            onClick={() => setActiveTab('saques')}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'saques'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <TrendingDown className="w-5 h-5" />
            <span>Saques</span>
          </button>
        </div>
      </div>

      {/* Render content based on active tab */}
      {activeTab === 'casas' ? (
        <>
          {/* Period Filter */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📅 Período dos Dados
                </label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="current_month">📅 Mês Atual</option>
                  <option value="last_month">📆 Mês Anterior</option>
                  <option value="last_3_months">📊 Últimos 3 Meses</option>
                  <option value="all_time">🌐 Todo o Período</option>
                  <option value="custom">🎯 Período Personalizado</option>
                </select>
              </div>
              
              {selectedPeriod === 'custom' && (
                <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Data inicial"
                  />
                  <span className="hidden sm:inline text-gray-500 dark:text-gray-400">até</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Data final"
                  />
                </div>
              )}
              
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 sm:mt-6">
                {selectedPeriod === 'current_month' && '📊 Mostrando resultado do mês atual'}
                {selectedPeriod === 'last_month' && '📊 Mostrando resultado do mês passado'}
                {selectedPeriod === 'last_3_months' && '📊 Mostrando resultado dos últimos 3 meses'}
                {selectedPeriod === 'all_time' && '📊 Mostrando resultado de todo o período'}
                {selectedPeriod === 'custom' && customStartDate && customEndDate && 
                  `📊 Período: ${new Date(customStartDate).toLocaleDateString('pt-BR')} - ${new Date(customEndDate).toLocaleDateString('pt-BR')}`
                }
              </div>
            </div>
          </div>

          {/* Stats Cards - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Banca Inicial Total</h3>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalInitial)}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Banca Atual Total</h3>
          <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(stats.totalCurrent)}</p>
          <p className="text-xs text-gray-500">Calculado pelas apostas</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Lucro/Prejuízo Total</h3>
          <p className={`text-xl sm:text-2xl font-bold ${stats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(stats.totalProfit)}
          </p>
          <p className="text-xs text-gray-500">Baseado em {bettingHouses.length} casas</p>
        </div>
      </div>

      {/* Betting Houses List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Casas de Apostas Cadastradas
          </h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Carregando...</p>
            </div>
          ) : bettingHouses.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhuma casa de apostas cadastrada
              </h3>
              <p className="text-gray-500 mb-6">
                Comece adicionando sua primeira casa de apostas para gerenciar suas bancas.
              </p>
              <button
                onClick={() => openModal()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Adicionar Casa de Apostas
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {bettingHouses.map(house => {
                const houseStats = getHouseStats(house.id);
                const periodDates = getPeriodDates();
                
                // Calcular BANCA INICIAL (início do período selecionado)
                let bancaInicial = 0;
                if (!periodDates) {
                  // Se não há período, banca inicial = total de depósitos iniciais
                  bancaInicial = allDeposits
                    .filter(d => d.betting_house_id === house.id && d.type === 'initial')
                    .reduce((sum, d) => sum + d.amount, 0);
                } else {
                  // Calcular saldo no INÍCIO do período
                  const startDate = new Date(periodDates.startDate);
                  
                  // Depósitos ANTES do início
                  const depositsBeforeStart = allDeposits.filter(d => {
                    const depositDate = new Date(d.date);
                    return d.betting_house_id === house.id && depositDate < startDate;
                  }).reduce((sum, d) => sum + d.amount, 0);
                  
                  // Lucro ANTES do início
                  const profitBeforeStart = allBets.filter(b => {
                    const betDate = new Date(b.date);
                    return b.betting_house_id === house.id && b.status === 'completed' && betDate < startDate;
                  }).reduce((sum, b) => sum + (b.profit || 0), 0);
                  
                  // Saques ANTES do início
                  const withdrawalsBeforeStart = allWithdrawals.filter(w => {
                    const withdrawalDate = new Date(w.date);
                    return w.betting_house_id === house.id && withdrawalDate < startDate;
                  }).reduce((sum, w) => sum + w.amount, 0);
                  
                  bancaInicial = depositsBeforeStart + profitBeforeStart - withdrawalsBeforeStart;
                }
                
                // Calcular BANCA FINAL (fim do período selecionado)
                let bancaFinal = 0;
                if (!periodDates) {
                  // Se não há período, banca final = saldo atual da casa
                  bancaFinal = house.current_bankroll || 0;
                } else {
                  // Calcular saldo no FIM do período
                  const endDate = new Date(periodDates.endDate);
                  
                  // Depósitos ATÉ o fim
                  const depositsUntilEnd = allDeposits.filter(d => {
                    const depositDate = new Date(d.date);
                    return d.betting_house_id === house.id && depositDate <= endDate;
                  }).reduce((sum, d) => sum + d.amount, 0);
                  
                  // Lucro ATÉ o fim
                  const profitUntilEnd = allBets.filter(b => {
                    const betDate = new Date(b.date);
                    return b.betting_house_id === house.id && b.status === 'completed' && betDate <= endDate;
                  }).reduce((sum, b) => sum + (b.profit || 0), 0);
                  
                  // Saques ATÉ o fim
                  const withdrawalsUntilEnd = allWithdrawals.filter(w => {
                    const withdrawalDate = new Date(w.date);
                    return w.betting_house_id === house.id && withdrawalDate <= endDate;
                  }).reduce((sum, w) => sum + w.amount, 0);
                  
                  bancaFinal = depositsUntilEnd + profitUntilEnd - withdrawalsUntilEnd;
                }
                
                // Depósitos DURANTE o período
                const depositosDoPeriodo = allDeposits.filter(d => {
                  if (!periodDates) return false;
                  const depositDate = new Date(d.date);
                  return d.betting_house_id === house.id && 
                         depositDate >= new Date(periodDates.startDate) && 
                         depositDate <= new Date(periodDates.endDate);
                }).reduce((sum, d) => sum + d.amount, 0);
                
                // Saques DURANTE o período
                const saquesDoPeriodo = allWithdrawals.filter(w => {
                  if (!periodDates) return false;
                  const withdrawalDate = new Date(w.date);
                  return w.betting_house_id === house.id && 
                         withdrawalDate >= new Date(periodDates.startDate) && 
                         withdrawalDate <= new Date(periodDates.endDate);
                }).reduce((sum, w) => sum + w.amount, 0);
                
                // Lucro do período = (Banca Final - Banca Inicial) - Depósitos + Saques
                const lucroDoPeriodo = (bancaFinal - bancaInicial) - depositosDoPeriodo + saquesDoPeriodo;
                
                // Apostas pendentes
                const pendingBets = allBets.filter(bet => 
                  bet.betting_house_id === house.id && 
                  bet.status === 'pending'
                );
                const pendingAmount = pendingBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
                
                // Para exibir corretamente
                const currentWithProfit = bancaFinal;
                const result = lucroDoPeriodo;
                const resultROI = bancaInicial > 0 ? (result / bancaInicial * 100) : 0;
                
                return (
                <div key={house.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">{house.name}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {houseStats.totalBets} apostas no período
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDepositModal(house)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Adicionar Depósito"
                      >
                        <DollarSign className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => openModal(house)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(house.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* 🆕 Gestão de Banca - Período Selecionado */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      💰 Gestão de Banca
                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                        ({selectedPeriod === 'all' ? 'Todo período' : selectedPeriod === 'month' ? 'Mês atual' : selectedPeriod === 'week' ? 'Última semana' : 'Personalizado'})
                      </span>
                    </h5>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {/* Banca Inicial do Período */}
                      {(() => {
                        // Calcular banca no início do período (saldo que existia ANTES do período começar)
                        let bankrollInicial = 0;
                        
                        const periodDates = getPeriodDates();
                        if (!periodDates) {
                          // Todo período = capital inicial original investido
                          bankrollInicial = allDeposits
                            .filter(d => d.betting_house_id === house.id && d.type === 'initial')
                            .reduce((sum, d) => sum + d.amount, 0);
                        } else {
                          // Calcular quanto tinha no início do período selecionado
                          const periodStart = new Date(periodDates.startDate);
                          
                          // Depósitos feitos ANTES do início do período
                          const depositsBeforeStart = allDeposits.filter(d => {
                            const depositDate = new Date(d.date);
                            return d.betting_house_id === house.id && depositDate < periodStart;
                          });
                          const totalDepositsBeforeStart = depositsBeforeStart.reduce((sum, d) => sum + d.amount, 0);
                          
                          // Lucro/prejuízo acumulado ANTES do início do período
                          const betsBeforeStart = allBets.filter(b => {
                            const betDate = new Date(b.date);
                            return b.betting_house_id === house.id && b.status === 'completed' && betDate < periodStart;
                          });
                          const profitBeforeStart = betsBeforeStart.reduce((sum, b) => sum + (b.profit || 0), 0);
                          
                          // Saques feitos ANTES do início do período
                          const withdrawalsBeforeStart = allWithdrawals.filter(w => {
                            const withdrawalDate = new Date(w.date);
                            return w.betting_house_id === house.id && withdrawalDate < periodStart;
                          });
                          const totalWithdrawalsBeforeStart = withdrawalsBeforeStart.reduce((sum, w) => sum + w.amount, 0);
                          
                          // Banca Inicial = Depósitos + Lucro - Saques (tudo ANTES do período)
                          bankrollInicial = totalDepositsBeforeStart + profitBeforeStart - totalWithdrawalsBeforeStart;
                        }
                        
                        return (
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Banca Inicial</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              {formatCurrency(bankrollInicial)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {periodDates ? 'Início do período' : 'Capital investido'}
                            </p>
                          </div>
                        );
                      })()}
                      
                      {/* Depósitos do Período */}
                      {house.additional_deposits > 0 && (
                        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Depósitos +</p>
                          <p className="text-sm font-bold text-green-600 dark:text-green-400">
                            +{formatCurrency(house.additional_deposits)}
                          </p>
                        </div>
                      )}
                      
                      {/* Saques do Período */}
                      {(() => {
                        const houseSaques = allWithdrawals.filter(w => w.betting_house_id === house.id);
                        const totalSaques = houseSaques.reduce((sum, w) => sum + w.amount, 0);
                        return totalSaques > 0 ? (
                          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Saques -</p>
                            <p className="text-sm font-bold text-red-600 dark:text-red-400">
                              -{formatCurrency(totalSaques)}
                            </p>
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Lucro do Período */}
                      <div className={`text-center p-3 rounded-lg ${houseStats.totalProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Lucro Período</p>
                        <p className={`text-sm font-bold ${houseStats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {houseStats.totalProfit >= 0 ? '+' : ''}{formatCurrency(houseStats.totalProfit)}
                        </p>
                      </div>
                      
                      {/* Banca Final do Período */}
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Banca Final</p>
                        <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                          {formatCurrency(currentWithProfit)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {!periodDates ? 'Saldo atual' : 'Fim do período'}
                        </p>
                        {pendingAmount > 0 && (
                          <p className="text-xs text-orange-600 dark:text-orange-400">
                            {pendingBets.length} pendente{pendingBets.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      
                      {/* ROI do Período */}
                      {(() => {
                        const roiPeriod = bancaInicial > 0 ? (houseStats.totalProfit / bancaInicial * 100) : 0;
                        return (
                          <div className={`text-center p-3 rounded-lg ${roiPeriod >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">ROI Período</p>
                            <p className={`text-sm font-bold ${roiPeriod >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {roiPeriod >= 0 ? '+' : ''}{roiPeriod.toFixed(1)}%
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Card de Explicação */}
                    <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        💡 <strong>Como funciona:</strong> A &quot;Banca Inicial&quot; é o saldo no INÍCIO do período. 
                        A &quot;Banca Final&quot; é o saldo no FIM do período selecionado. O &quot;Lucro do Período&quot; já desconta depósitos e saques feitos durante o período.
                      </p>
                    </div>
                  </div>

                  {/* Performance no Período */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-blue-900/20 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      📊 Performance no Período
                    </h5>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Apostas</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{houseStats.totalBets}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Green</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{houseStats.greenBets}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Red</p>
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">{houseStats.redBets}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Taxa Acerto</p>
                        <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{houseStats.winRate.toFixed(0)}%</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Volume</p>
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{formatCurrency(houseStats.totalVolume)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Resultado Final */}
                  <div className={`rounded-lg p-4 ${result >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="text-center sm:text-left">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">🎯 Resultado no Período</p>
                        <p className={`text-xl sm:text-2xl font-bold ${result >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {result >= 0 ? '+' : ''}{formatCurrency(result)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">📊 ROI Banca</p>
                        <p className={`text-xl sm:text-2xl font-bold ${resultROI >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {resultROI >= 0 ? '+' : ''}{resultROI.toFixed(1)}%
                        </p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">📈 ROI Apostas</p>
                        <p className={`text-xl sm:text-2xl font-bold ${houseStats.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {houseStats.roi >= 0 ? '+' : ''}{houseStats.roi.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal - Mobile Optimized */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {editingHouse ? 'Editar Casa de Apostas' : 'Nova Casa de Apostas'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome da Casa de Apostas *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Bet365, Betfair..."
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {editingHouse ? 'Banca Inicial (R$)' : 'Banca Inicial (R$) *'}
                </label>
                <input
                  type="number"
                  name="initial_bankroll"
                  required={!editingHouse}
                  disabled={editingHouse}
                  step="0.01"
                  min="0"
                  value={formData.initial_bankroll}
                  onChange={handleInputChange}
                  placeholder="Ex: 1000.00"
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                />
              </div>

              {editingHouse && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Banca Atual (R$) *
                  </label>
                  <input
                    type="number"
                    name="current_bankroll"
                    required
                    step="0.01"
                    min="0"
                    value={formData.current_bankroll}
                    onChange={handleInputChange}
                    placeholder="Ex: 1200.00"
                    className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : editingHouse ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Depósito */}
      {showDepositModal && depositingHouse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                💰 Adicionar Depósito
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {depositingHouse.name}
              </p>
            </div>
            <form onSubmit={handleDeposit} className="p-4 sm:p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Banca Inicial:</strong> {formatCurrency(depositingHouse.initial_bankroll)}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  <strong>Banca Atual:</strong> {formatCurrency(depositingHouse.current_bankroll)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor do Depósito (R$) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="Ex: 500.00"
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data do Depósito *
                </label>
                <input
                  type="date"
                  required
                  value={depositDate}
                  onChange={(e) => setDepositDate(e.target.value)}
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {depositAmount && parseFloat(depositAmount) > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Nova Inicial:</strong> {formatCurrency(depositingHouse.initial_bankroll + parseFloat(depositAmount))}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    <strong>Nova Atual:</strong> {formatCurrency(depositingHouse.current_bankroll + parseFloat(depositAmount))}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeDepositModal}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Adicionando...' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Histórico de Depósitos */}
      {showDepositsHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                💰 Gerenciar Depósitos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Histórico completo de todos os depósitos
              </p>
            </div>
            
            <div className="p-4 sm:p-6">
              {allDeposits.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhum depósito registrado ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allDeposits.map(deposit => {
                    const house = bettingHouses.find(h => h.id === deposit.betting_house_id);
                    return (
                      <div key={deposit.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              deposit.type === 'initial' 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {deposit.type === 'initial' ? 'Inicial' : 'Adicional'}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {house ? house.name : 'Casa excluída'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {deposit.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-gray-700 dark:text-gray-300">
                              📅 {new Date(deposit.date).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="font-semibold text-green-600 dark:text-green-400">
                              R$ {deposit.amount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditDeposit(deposit)}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                          >
                            ✏️ Editar
                          </button>
                          <button
                            onClick={() => handleDeleteDeposit(deposit.id)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                          >
                            🗑️ Excluir
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDepositsHistoryModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Depósito */}
      {showEditDepositModal && editingDeposit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                ✏️ Editar Depósito
              </h3>
            </div>
            <form onSubmit={handleUpdateDeposit} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data *
                </label>
                <input
                  type="date"
                  required
                  value={depositDate}
                  onChange={(e) => setDepositDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditDepositModal(false);
                    setEditingDeposit(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      ) : (
        /* Saques Tab */
        <WithdrawalsManagement />
      )}
    </div>
  );
};

export default BankrollManagement;